import {

  useEffect,

  useMemo,

  useState,

} from "react";

import CrudModal from "components/crud/CrudModal";

import CrudInput from "components/crud/CrudInput";

import CrudSelect from "components/crud/CrudSelect";

import {

  getSchools,

} from "services/api/schoolApi";

import {

  getParties,

} from "services/api/partyApi";

import {

  createJourneyPlan,

  updateJourneyPlan,

} from "services/api/journeyPlanApi";

import {

  showSuccess,

  showError,

} from "utils/toast";

// ============================================================

// INITIAL FORM

// ============================================================

const createInitialForm = () => ({

  planDate: "",

  title: "",

  remarks: "",

  items: [],

});

// ============================================================

// COMPONENT

// ============================================================

export default function JourneyPlanModal({

  open,

  onClose,

  reload,

  item,

}) {

  const isEdit = Boolean(item);

  // ============================================================

  // STATE

  // ============================================================

  const [

    loading,

    setLoading,

  ] = useState(false);

  const [

    form,

    setForm,

  ] = useState(

    createInitialForm()

  );

  const [

    schools,

    setSchools,

  ] = useState([]);

  const [

    parties,

    setParties,

  ] = useState([]);

  // ============================================================

  // LOAD SCHOOLS + PARTIES

  // ============================================================

  useEffect(() => {

    if (!open) {

      return;

    }

    const loadData = async () => {

      try {

        const [

          schoolRes,

          partyRes,

        ] = await Promise.all([

          getSchools(

            1,

            500

          ),

          getParties(

            1,

            500

          ),

        ]);

        const schoolData =

          schoolRes?.data?.schools ||

          schoolRes?.data?.data?.schools ||

          schoolRes?.schools ||

          schoolRes?.data?.records ||

          schoolRes?.data?.data?.records ||

          [];

        const partyData =

          partyRes?.data?.parties ||

          partyRes?.data?.data?.parties ||

          partyRes?.parties ||

          partyRes?.data?.records ||

          partyRes?.data?.data?.records ||

          [];

        setSchools(

          Array.isArray(schoolData)

            ? schoolData

            : []

        );

        setParties(

          Array.isArray(partyData)

            ? partyData

            : []

        );

      } catch (error) {

        console.error(

          "Journey Plan dropdown loading error:",

          error

        );

        setSchools([]);

        setParties([]);

        showError(

          error?.response?.data?.message ||

          "Unable to load schools and parties."

        );

      }

    };

    loadData();

    // ==========================================================

    // EDIT MODE

    // ==========================================================

    if (item) {

      setForm({

        planDate:

          item.planDate

            ?.substring(0, 10) ||

          "",

        title:

          item.title ||

          "",

        remarks:

          item.remarks ||

          "",

        items:

          Array.isArray(item.items)

            ? item.items.map(

                (visit, index) => ({

                  itemType:

                    visit.itemType ||

                    "school",

                  schoolId:

                    String(

                      visit.school?.id ||

                      visit.schoolId ||

                      ""

                    ),

                  partyId:

                    String(

                      visit.party?.id ||

                      visit.partyId ||

                      ""

                    ),

                  sequence:

                    visit.sequence ||

                    index + 1,

                  priority:

                    visit.priority ||

                    "Medium",

                  purpose:

                    visit.purpose ||

                    "",

                  plannedTime:

                    visit.plannedTime ||

                    "",

                  claimAmount:

                    visit.claimAmount !== null &&

                    visit.claimAmount !== undefined

                      ? String(visit.claimAmount)

                      : "",

                  remarks:

                    visit.remarks ||

                    "",

                })

              )

            : [],

      });

    } else {

      setForm(

        createInitialForm()

      );

    }

  }, [

    open,

    item,

  ]);

  // ============================================================

  // LOCATION HELPERS

  // ============================================================

  const getLocationName = (

    school,

    key

  ) => {

    return (

      school?.location?.[key]?.name ||

      school?.[key]?.name ||

      ""

    );

  };

  const getSchoolLocation = (

    school

  ) => {

    if (!school) {

      return {

        city: "",

        district: "",

        state: "",

        area: "",

        beat: "",

        zone: "",

      };

    }

    return {

      city:

        getLocationName(

          school,

          "city"

        ),

      district:

        getLocationName(

          school,

          "district"

        ),

      state:

        getLocationName(

          school,

          "state"

        ),

      area:

        getLocationName(

          school,

          "area"

        ),

      beat:

        getLocationName(

          school,

          "beat"

        ),

      zone:

        getLocationName(

          school,

          "zone"

        ),

    };

  };

  const getBeatZone = (

    school

  ) => {

    const location =

      getSchoolLocation(

        school

      );

    return (

      location.beat ||

      location.zone ||

      ""

    );

  };

  // ============================================================

  // SCHOOL OPTIONS

  // ============================================================

  const schoolOptions = useMemo(() => {

    return schools.map(

      (school) => {

        const location =

          getSchoolLocation(

            school

          );

        const locationParts = [

          location.city,

          location.district,

          location.state,

        ].filter(Boolean);

        const locationText =

          locationParts.length

            ? ` - ${locationParts.join(", ")}`

            : "";

        return {

          value:

            String(

              school.id

            ),

          label:

            `${school.name || "School"}${locationText}`,

        };

      }

    );

  }, [schools]);

  // ============================================================

  // PARTY OPTIONS

  // ============================================================

  const partyOptions = useMemo(() => {

    return parties.map(

      (party) => ({

        value:

          String(

            party.id

          ),

        label:

          party.name ||

          party.partyCode ||

          "Party",

      })

    );

  }, [parties]);

  // ============================================================

  // SELECTED SCHOOL

  // ============================================================

  const getSelectedSchool = (

    schoolId

  ) => {

    if (!schoolId) {

      return null;

    }

    return (

      schools.find(

        (school) =>

          String(

            school.id

          ) ===

          String(

            schoolId

          )

      ) ||

      null

    );

  };

  // ============================================================

  // SCHOOL LOCATION CARD

  // ============================================================

  const renderSchoolLocation = (

    school

  ) => {

    if (!school) {

      return null;

    }

    const location =

      getSchoolLocation(

        school

      );

    const beatZone =

      getBeatZone(

        school

      );

    const hasLocation =

      Boolean(

        school?.name ||

        location.city ||

        location.district ||

        location.state ||

        location.area ||

        beatZone

      );

    if (!hasLocation) {

      return (

        <div

          className="

            mt-3

            rounded-xl

            border

            border-yellow-200

            bg-yellow-50

            p-4

          "

        >

          <p

            className="

              text-sm

              font-medium

              text-yellow-800

            "

          >

            Location information is not

            available for this school.

          </p>

        </div>

      );

    }

    return (

      <div

        className="

          mt-3

          rounded-xl

          border

          border-blue-200

          bg-blue-50

          p-4

        "

      >

        {/* ==================================================

            HEADER

        ================================================== */}

        <div

          className="

            mb-4

          "

        >

          <p

            className="

              text-xs

              font-semibold

              uppercase

              tracking-wide

              text-blue-600

            "

          >

            Visit Location

          </p>

          <p

            className="

              mt-1

              text-base

              font-bold

              text-blue-950

            "

          >

            {school.name || "-"}

          </p>

        </div>

        {/* ==================================================

            LOCATION GRID

        ================================================== */}

        <div

          className="

            grid

            grid-cols-1

            gap-3

            md:grid-cols-2

            lg:grid-cols-3

          "

        >

          {/* CITY / TOWN */}

          {location.city && (

            <div

              className="

                rounded-lg

                bg-white

                p-3

              "

            >

              <p

                className="

                  text-[11px]

                  font-semibold

                  uppercase

                  tracking-wide

                  text-gray-500

                "

              >

                City / Town

              </p>

              <p

                className="

                  mt-1

                  text-sm

                  font-semibold

                  text-gray-800

                "

              >

                {location.city}

              </p>

            </div>

          )}

          {/* DISTRICT */}

          {location.district && (

            <div

              className="

                rounded-lg

                bg-white

                p-3

              "

            >

              <p

                className="

                  text-[11px]

                  font-semibold

                  uppercase

                  tracking-wide

                  text-gray-500

                "

              >

                District

              </p>

              <p

                className="

                  mt-1

                  text-sm

                  font-semibold

                  text-gray-800

                "

              >

                {location.district}

              </p>

            </div>

          )}

          {/* STATE */}

          {location.state && (

            <div

              className="

                rounded-lg

                bg-white

                p-3

              "

            >

              <p

                className="

                  text-[11px]

                  font-semibold

                  uppercase

                  tracking-wide

                  text-gray-500

                "

              >

                State

              </p>

              <p

                className="

                  mt-1

                  text-sm

                  font-semibold

                  text-gray-800

                "

              >

                {location.state}

              </p>

            </div>

          )}

          {/* AREA / LOCALITY */}

          {location.area && (

            <div

              className="

                rounded-lg

                bg-white

                p-3

              "

            >

              <p

                className="

                  text-[11px]

                  font-semibold

                  uppercase

                  tracking-wide

                  text-gray-500

                "

              >

                Area / Locality

              </p>

              <p

                className="

                  mt-1

                  text-sm

                  font-semibold

                  text-gray-800

                "

              >

                {location.area}

              </p>

            </div>

          )}

          {/* BEAT / ZONE */}

          {beatZone && (

            <div

              className="

                rounded-lg

                bg-white

                p-3

              "

            >

              <p

                className="

                  text-[11px]

                  font-semibold

                  uppercase

                  tracking-wide

                  text-gray-500

                "

              >

                Beat / Zone

              </p>

              <p

                className="

                  mt-1

                  text-sm

                  font-semibold

                  text-gray-800

                "

              >

                {beatZone}

              </p>

            </div>

          )}

        </div>

      </div>

    );

  };

  // ============================================================

  // ADD VISIT

  // ============================================================

  const addVisit = () => {

    setForm(

      (prev) => ({

        ...prev,

        items: [

          ...prev.items,

          {

            itemType:

              "school",

            schoolId:

              "",

            partyId:

              "",

            sequence:

              prev.items.length + 1,

            priority:

              "Medium",

            purpose:

              "",

            plannedTime:

              "",

            claimAmount:

              "",

            remarks:

              "",

          },

        ],

      })

    );

  };

  // ============================================================

  // REMOVE VISIT

  // ============================================================

  const removeVisit = (

    index

  ) => {

    setForm(

      (prev) => ({

        ...prev,

        items:

          prev.items.filter(

            (_, itemIndex) =>

              itemIndex !== index

          ),

      })

    );

  };

  // ============================================================

  // UPDATE VISIT

  // ============================================================

  const updateVisit = (

    index,

    key,

    value

  ) => {

    setForm(

      (prev) => ({

        ...prev,

        items:

          prev.items.map(

            (

              visit,

              itemIndex

            ) =>

              itemIndex === index

                ? {

                    ...visit,

                    [key]:

                      value,

                  }

                : visit

          ),

      })

    );

  };

  // ============================================================

  // SUBMIT

  // ============================================================

  const submit = async (

    e

  ) => {

    e.preventDefault();

    try {

      setLoading(true);

      const payload = {

        planDate:

          form.planDate,

        title:

          form.title ||

          undefined,

        remarks:

          form.remarks ||

          undefined,

        items:

          form.items.map(

            (

              visit,

              index

            ) => ({

              itemType:

                visit.itemType,

              schoolId:

                visit.itemType ===

                "school"

                  ? visit.schoolId ||

                    undefined

                  : undefined,

              partyId:

                visit.itemType ===

                "party"

                  ? visit.partyId ||

                    undefined

                  : undefined,

              sequence:

                index + 1,

              priority:

                visit.priority ||

                undefined,

              purpose:

                visit.purpose ||

                undefined,

              plannedTime:

                visit.plannedTime ||

                undefined,

              claimAmount:

                visit.claimAmount !== "" &&

                visit.claimAmount !== null &&

                visit.claimAmount !== undefined

                  ? Number(visit.claimAmount)

                  : undefined,

              remarks:

                visit.remarks ||

                undefined,

            })

          ),

      };

      if (isEdit) {

        await updateJourneyPlan(

          item.id,

          payload

        );

        showSuccess(

          "Journey plan updated."

        );

      } else {

        await createJourneyPlan(

          payload

        );

        showSuccess(

          "Journey plan created."

        );

      }

      await reload();

      onClose();

    } catch (error) {

      console.error(

        "Journey plan save error:",

        error

      );

      showError(

        error?.response?.data?.message ||

        "Unable to save journey plan."

      );

    } finally {

      setLoading(false);

    }

  };

  // ============================================================

  // CLOSED

  // ============================================================

  if (!open) {

    return null;

  }

  // ============================================================

  // UI

  // ============================================================

  return (

    <CrudModal

      open={open}

      onClose={onClose}

      onSubmit={submit}

      loading={loading}

      title={

        isEdit

          ? "Edit Journey Plan"

          : "Create Journey Plan"

      }

      submitText={

        isEdit

          ? "Update"

          : "Save"

      }

      width="max-w-6xl"

    >

      {/* ========================================================

          PLAN DETAILS

      ======================================================== */}

      <CrudInput

        label="Plan Date"

        type="date"

        value={

          form.planDate

        }

        onChange={(e) =>

          setForm(

            (prev) => ({

              ...prev,

              planDate:

                e.target.value,

            })

          )

        }

      />

      <CrudInput

        label="Title"

        value={

          form.title

        }

        onChange={(e) =>

          setForm(

            (prev) => ({

              ...prev,

              title:

                e.target.value,

            })

          )

        }

      />

      <CrudInput

        label="Remarks"

        value={

          form.remarks

        }

        onChange={(e) =>

          setForm(

            (prev) => ({

              ...prev,

              remarks:

                e.target.value,

            })

          )

        }

      />

      {/* ========================================================

          ADD VISIT

      ======================================================== */}

      <div

        className="

          mb-5

          flex

          items-center

          justify-between

        "

      >

        <div>

          <p

            className="

              text-sm

              font-semibold

              text-gray-800

            "

          >

            Scheduled Visits

          </p>

          <p

            className="

              mt-1

              text-xs

              text-gray-500

            "

          >

            Add schools or parties to the journey plan.

          </p>

        </div>

        <button

          type="button"

          onClick={

            addVisit

          }

          className="

            rounded-lg

            bg-brand-500

            px-4

            py-2

            text-sm

            font-semibold

            text-white

            transition

            hover:bg-brand-600

          "

        >

          Add Visit

        </button>

      </div>

      {/* ========================================================

          VISITS

      ======================================================== */}

      {form.items.map(

        (

          visit,

          index

        ) => {

          const selectedSchool =

            visit.itemType ===

            "school"

              ? getSelectedSchool(

                  visit.schoolId

                )

              : null;

          return (

            <div

              key={

                `journey-item-${index}`

              }

              className="

                mb-5

                rounded-xl

                border

                border-gray-200

                bg-white

                p-5

              "

            >

              {/* VISIT HEADER */}

              <div

                className="

                  mb-4

                  flex

                  items-center

                  justify-between

                "

              >

                <div>

                  <p

                    className="

                      text-sm

                      font-bold

                      text-gray-800

                    "

                  >

                    Visit {index + 1}

                  </p>

                  <p

                    className="

                      mt-1

                      text-xs

                      text-gray-500

                    "

                  >

                    Scheduled visit details

                  </p>

                </div>

                <button

                  type="button"

                  onClick={() =>

                    removeVisit(

                      index

                    )

                  }

                  className="

                    text-sm

                    font-semibold

                    text-red-500

                    hover:underline

                  "

                >

                  Remove

                </button>

              </div>

              {/* TYPE */}

              <CrudSelect

                label="Type"

                value={

                  visit.itemType

                }

                onChange={(e) =>

                  updateVisit(

                    index,

                    "itemType",

                    e.target.value

                  )

                }

                options={[

                  {

                    value:

                      "school",

                    label:

                      "School",

                  },

                  {

                    value:

                      "party",

                    label:

                      "Party",

                  },

                ]}

              />

              {/* ==================================================

                  SCHOOL

              ================================================== */}

              {visit.itemType ===

                "school" && (

                <>

                  <CrudSelect

                    label="School"

                    value={

                      visit.schoolId

                    }

                    onChange={(e) =>

                      updateVisit(

                        index,

                        "schoolId",

                        e.target.value

                      )

                    }

                    options={

                      schoolOptions

                    }

                  />

                  {renderSchoolLocation(

                    selectedSchool

                  )}

                </>

              )}

              {/* ==================================================

                  PARTY

              ================================================== */}

              {visit.itemType ===

                "party" && (

                <CrudSelect

                  label="Party"

                  value={

                    visit.partyId

                  }

                  onChange={(e) =>

                    updateVisit(

                      index,

                      "partyId",

                      e.target.value

                    )

                  }

                  options={

                    partyOptions

                  }

                />

              )}

              {/* PURPOSE */}

              <CrudInput

                label="Purpose"

                value={

                  visit.purpose

                }

                onChange={(e) =>

                  updateVisit(

                    index,

                    "purpose",

                    e.target.value

                  )

                }

              />

              {/* PLANNED TIME */}

              <CrudInput

                label="Planned Time"

                type="time"

                value={

                  visit.plannedTime

                }

                onChange={(e) =>

                  updateVisit(

                    index,

                    "plannedTime",

                    e.target.value

                  )

                }

              />

              {/* CLAIM AMOUNT */}

              <CrudInput

                label="Claim Amount (₹)"

                type="number"

                min="0"

                step="0.01"

                value={

                  visit.claimAmount

                }

                onChange={(e) =>

                  updateVisit(

                    index,

                    "claimAmount",

                    e.target.value

                  )

                }

              />

              {/* REMARKS */}

              <CrudInput

                label="Visit Remarks"

                value={

                  visit.remarks

                }

                onChange={(e) =>

                  updateVisit(

                    index,

                    "remarks",

                    e.target.value

                  )

                }

              />

            </div>

          );

        }

      )}

      {/* ========================================================

          EMPTY STATE

      ======================================================== */}

      {form.items.length === 0 && (

        <div

          className="

            rounded-xl

            border

            border-dashed

            border-gray-300

            bg-gray-50

            p-6

            text-center

          "

        >

          <p

            className="

              text-sm

              font-medium

              text-gray-600

            "

          >

            No visits added yet.

          </p>

          <p

            className="

              mt-1

              text-xs

              text-gray-400

            "

          >

            Click "Add Visit" to schedule

            a school or party visit.

          </p>

        </div>

      )}

    </CrudModal>

  );

}